import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { Image } from "uikit-react";
import moment from "moment";

import { AppContexts } from "../../../providers";
import InspectionBoardService from "../../../services/inspection_board.service";
import ReservationsService from "../../../services/reservations.service";
import SearchableDropdown from "../../common/SearchableDropdown";
import ReservationType from "../../common/ReservationType";
import ReservationNumber from "../../common/ReservationNumber";
import GuestMessages from "../../guests/guest_messages";

// Status legend (colors come from SCSS via .is-<key> modifier classes)
export const INSPECTION_STATUSES = [
  { key: "ready",             label: "Ready for Check-in" },
  { key: "no_early_check_in", label: "No Early Check-in Allowed" },
  { key: "out_in",            label: "Out and In" },
  { key: "arrival",           label: "Arrival Check / Open" },
  { key: "maintenance",       label: "Maintenance Issues" },
  { key: "deep_clean",        label: "Deep Clean" },
  { key: "stale_clean",       label: "Stale Clean" },
  { key: "inspection",        label: "Mandatory Inspection" },
];

const CATEGORY_META = {
  STA: { label: "Guest" },
  OWN: { label: "Owner" },
  BLK: { label: "Maintenance Block" },
};

const fmtDateLong = (d) => moment.utc(d).format("ddd, MMM D, YYYY");
const fmtDateRange = (d) => moment.utc(d).format("MMM D, YYYY");
const DAYS_VACANT_LOOKBACK_DAYS = 10;

const getPropertyKeys = (res = {}) =>
  Array.from(
    new Set(
      [
        res.pms_id && `pms:${String(res.pms_id).trim().toLowerCase()}`,
      ].filter(Boolean),
    ),
  );

const Inspections = () => {
  const { searchReservation, listReservations, setReservation } = useContext(
    AppContexts.ReservationsContext,
  );
  const { rentals, get: getRentals } = useContext(AppContexts.RentalsContext);
  const settingsContext = useContext(AppContexts.SettingsContext);
  const { user } = useContext(AppContexts.AuthContext);

  const [view, setView] = useState("daily"); // "daily" | "weekly"
  const [anchor, setAnchor] = useState(moment().startOf("day"));
  const [tagFilter, setTagFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);
  // entries keyed by guest_reservation_id: { statuses: {key:bool}, note: string, handled: bool }
  const [entries, setEntries] = useState({});
  // priorCheckouts keyed by pms_id: sorted array of check_out date strings (YYYY-MM-DD)
  const [priorCheckouts, setPriorCheckouts] = useState(null);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const noteTimers = useRef({});

  useEffect(() => {
    document.title = "Inspection Board - RueBaRue";
  }, []);

  useEffect(() => {
    getRentals();
    settingsContext.get(["tags"]);
  }, []);

  const tagOptions = useMemo(
    () => settingsContext.settings?.tags || [],
    [settingsContext.settings],
  );

  // Compute window
  const { rangeStart, rangeEnd } = useMemo(() => {
    if (view === "daily") {
      return {
        rangeStart: anchor.clone().startOf("day"),
        rangeEnd: anchor.clone().endOf("day"),
      };
    }
    return {
      rangeStart: anchor.clone().startOf("week"),
      rangeEnd: anchor.clone().endOf("week"),
    };
  }, [view, anchor]);

  // Fetch reservations + saved entries for the window
  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const startStr = rangeStart.clone().add(-1, "days").format("YYYY-MM-DD");
    const endStr = rangeEnd.clone().add(1, "days").format("YYYY-MM-DD");

    const reservationCriteria = {
      search: "",
      filters: "owners,blocks,standard",
      preset: "staying-between",
      start_date: startStr,
      end_date: endStr,
      end_type: "check_in",
      categories: ["OWN", "BLK", "STA", ""],
      tag: "",
      page: 0,
      per_page: -1,
      checked_in: false,
      check_out: false,
    };

    // Window of prior check-outs used to compute "Days Vacant" for each
    // arrival row. Cap the lookup so large accounts do not load a year of
    // reservation history just for this column.
    const priorStart = rangeStart
      .clone()
      .subtract(DAYS_VACANT_LOOKBACK_DAYS, "days")
      .format("YYYY-MM-DD");
    const priorEnd = rangeEnd.format("YYYY-MM-DD");
    const priorCriteria = {
      search: "",
      filters: "owners,blocks,standard",
      preset: "departing-on",
      start_date: priorStart,
      end_date: priorEnd,
      categories: ["OWN", "BLK", "STA", ""],
      tag: "",
      page: 0,
      per_page: -1,
      checked_in: false,
      check_out: false,
    };

    Promise.all([
      Promise.resolve(searchReservation(reservationCriteria)),
      InspectionBoardService.list({
        start_date: rangeStart.format("YYYY-MM-DD"),
        end_date: rangeEnd.format("YYYY-MM-DD"),
      })
        .then((r) => r.data || [])
        .catch((err) => {
          console.error("inspection-board fetch failed", err);
          return [];
        }),
      ReservationsService.searchReservation(priorCriteria)
        .then((r) => r?.data?.reservations || [])
        .catch((err) => {
          console.error("inspection-board prior-checkouts fetch failed", err);
          return null;
        }),
    ])
      .then(([, board, prior]) => {
        if (cancelled) return;
        const map = {};
        (board || []).forEach((row) => {
          map[String(row.guest_reservation_id)] = {
            statuses: row.statuses || {},
            note: row.note || "",
            handled: !!row.handled,
          };
        });
        setEntries(map);

        if (prior === null) {
          setPriorCheckouts(null);
          return;
        }

        const checkouts = {};
        (prior || []).forEach((r) => {
          const propertyKeys = getPropertyKeys(r);
          if (propertyKeys.length === 0 || !r.check_out) return;
          const day = moment.utc(r.check_out).format("YYYY-MM-DD");
          propertyKeys.forEach((key) => {
            if (!checkouts[key]) checkouts[key] = [];
            checkouts[key].push(day);
          });
        });
        Object.keys(checkouts).forEach((k) => {
          // ascending sort; deduplicate to keep array small
          checkouts[k] = Array.from(new Set(checkouts[k])).sort();
        });
        setPriorCheckouts(checkouts);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [rangeStart.valueOf(), rangeEnd.valueOf()]);

  const rentalByPmsId = useMemo(() => {
    const map = {};
    (rentals || []).forEach((r) => {
      if (r.pms_id) map[r.pms_id] = r;
    });
    return map;
  }, [rentals]);

  // Days the property has been vacant prior to this arrival, computed as the
  // gap between the most recent prior check-out for this pms_id and this
  // reservation's check_in. Returns null when we have no prior check-out info.
  const daysVacantFor = useCallback(
    (res) => {
      if (!res?.check_in || !priorCheckouts) return null;
      const propertyKeys = getPropertyKeys(res);
      if (propertyKeys.length === 0) return null;
      const list = Array.from(
        new Set(propertyKeys.flatMap((key) => priorCheckouts[key] || [])),
      ).sort();
      if (!list || list.length === 0) return `${DAYS_VACANT_LOOKBACK_DAYS}+`;
      const checkInDay = moment.utc(res.check_in).format("YYYY-MM-DD");
      // Find the latest check-out strictly on or before the arrival day.
      let prior = null;
      for (let i = list.length - 1; i >= 0; i -= 1) {
        if (list[i] <= checkInDay) {
          prior = list[i];
          break;
        }
      }
      if (!prior) return `${DAYS_VACANT_LOOKBACK_DAYS}+`;
      const days = moment
        .utc(checkInDay)
        .diff(moment.utc(prior), "days");
      if (days > DAYS_VACANT_LOOKBACK_DAYS) {
        return `${DAYS_VACANT_LOOKBACK_DAYS}+`;
      }
      return days < 0 ? null : days;
    },
    [priorCheckouts],
  );

  // Group reservations by check-in date within the active window
  const grouped = useMemo(() => {
    const days = {};
    const cursor = rangeStart.clone();
    while (cursor.isSameOrBefore(rangeEnd, "day")) {
      days[cursor.format("YYYY-MM-DD")] = [];
      cursor.add(1, "day");
    }

    (listReservations || []).forEach((res) => {
      // tag filter (matches rental tags merged onto the reservation)
      if (tagFilter) {
        const rental = rentalByPmsId[res.pms_id];
        const tagList = res.tags || res.tag_array || rental?.tags || [];
        const hit = tagList.some(
          (t) => String(t?.id ?? t) === String(tagFilter),
        );
        if (!hit) return;
      }
      // status filter
      if (statusFilter) {
        const idStr = String(res.guest_reservation_id);
        const cur = entries[idStr];
        if (statusFilter === "none") {
          const hasAny = cur?.statuses
            && Object.values(cur.statuses).some(Boolean);
          if (hasAny) return;
        } else if (!cur?.statuses?.[statusFilter]) {
          return;
        }
      }
      const ci = moment.utc(res.check_in);
      const key = ci.format("YYYY-MM-DD");
      if (Object.prototype.hasOwnProperty.call(days, key)) {
        days[key].push(res);
      }
    });

    Object.keys(days).forEach((k) => {
      days[k].sort((a, b) =>
        (a.rental_name || a.pms_id || "").localeCompare(
          b.rental_name || b.pms_id || "",
        ),
      );
    });
    return days;
  }, [
    listReservations,
    rangeStart.valueOf(),
    rangeEnd.valueOf(),
    tagFilter,
    statusFilter,
    entries,
    rentalByPmsId,
  ]);

  // ----- mutations -------------------------------------------------------
  const persist = useCallback((reservationId, body) => {
    return InspectionBoardService.upsert(reservationId, body).catch((err) => {
      console.error("inspection-board save failed", err);
    });
  }, []);

  const toggleStatus = useCallback(
    (res, statusKey) => {
      const id = res.guest_reservation_id;
      if (!id) return;
      const idStr = String(id);

      setEntries((prev) => {
        const cur = prev[idStr] || { statuses: {}, note: "" };
        const wasActive = !!cur.statuses?.[statusKey];
        // Single-select: only the clicked key is on; clicking the active key clears it.
        const nextStatuses = wasActive ? {} : { [statusKey]: true };
        const nextEntry = { ...cur, statuses: nextStatuses };
        persist(id, { statuses: nextStatuses, note: nextEntry.note });
        return { ...prev, [idStr]: nextEntry };
      });
    },
    [persist],
  );

  const handleSMS = useCallback(
    (reservation) => {
      setReservation({
        ...reservation,
        formatted_phone: reservation.formatted_phone || reservation.phone,
      });
    },
    [setReservation],
  );

  const toggleHandled = useCallback(
    (res) => {
      const id = res.guest_reservation_id;
      if (!id) return;
      const idStr = String(id);

      setEntries((prev) => {
        const cur = prev[idStr] || { statuses: {}, note: "", handled: false };
        const nextEntry = { ...cur, handled: !cur.handled };
        persist(id, {
          statuses: nextEntry.statuses || {},
          note: nextEntry.note || "",
          handled: nextEntry.handled,
        });
        return { ...prev, [idStr]: nextEntry };
      });
    },
    [persist],
  );

  const updateNote = useCallback(
    (res, value) => {
      const id = res.guest_reservation_id;
      if (!id) return;
      const idStr = String(id);

      setEntries((prev) => {
        const cur = prev[idStr] || { statuses: {}, note: "" };
        return { ...prev, [idStr]: { ...cur, note: value } };
      });

      // debounce save per-reservation
      if (noteTimers.current[idStr]) clearTimeout(noteTimers.current[idStr]);
      noteTimers.current[idStr] = setTimeout(() => {
        setEntries((prev) => {
          const cur = prev[idStr] || { statuses: {}, note: "" };
          persist(id, { statuses: cur.statuses || {}, note: value });
          return prev;
        });
      }, 600);
    },
    [persist],
  );

  // ----- header / nav helpers --------------------------------------------
  const goPrev = () =>
    setAnchor(anchor.clone().subtract(1, view === "daily" ? "day" : "week"));
  const goNext = () =>
    setAnchor(anchor.clone().add(1, view === "daily" ? "day" : "week"));
  const goToday = () => setAnchor(moment().startOf("day"));

  const headerLabel =
    view === "daily"
      ? fmtDateLong(anchor)
      : `${rangeStart.format("MMM D")} – ${rangeEnd.format("MMM D, YYYY")}`;

  // ----- row rendering ---------------------------------------------------
  const renderStatusCell = (res) => {
    const cur = entries[String(res.guest_reservation_id)] || {};
    const statuses = cur.statuses || {};
    const activeStatus = INSPECTION_STATUSES.find((s) => statuses[s.key]);
    return (
      <div className="rbr-inspection-status-cell">
        <button
          type="button"
          className={`rbr-inspection-status-select${activeStatus ? ` is-${activeStatus.key} is-active` : ""}`}
        >
          <span
            className={`rbr-inspection-status-swatch${activeStatus ? ` is-${activeStatus.key}` : ""}`}
          />
          <span className="rbr-inspection-status-label">
            {activeStatus ? activeStatus.label : "Set status"}
          </span>
          <span uk-icon="icon: triangle-down; ratio: 0.85" />
        </button>
        <div
          data-uk-dropdown="mode: click; pos: bottom-left; offset: 4"
          className="rbr-inspection-status-dropdown"
        >
          <ul className="uk-nav uk-dropdown-nav rbr-inspection-status-menu">
            {INSPECTION_STATUSES.map((s) => {
              const active = !!statuses[s.key];
              return (
                <li key={s.key} className={`is-${s.key}${active ? " is-active" : ""}`}>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      toggleStatus(res, s.key);
                    }}
                  >
                    <span
                      className={`rbr-inspection-status-swatch is-${s.key}`}
                    />
                    <span className="rbr-inspection-status-label">{s.label}</span>
                    {active && (
                      <span
                        className="rbr-inspection-status-check"
                        uk-icon="icon: check; ratio: 0.85"
                      />
                    )}
                  </a>
                </li>
              );
            })}
            {activeStatus && (
              <>
                <li className="uk-nav-divider" />
                <li>
                  <a
                    href="#"
                    className="rbr-inspection-status-clear"
                    onClick={(e) => {
                      e.preventDefault();
                      toggleStatus(res, activeStatus.key);
                    }}
                  >
                    Clear status
                  </a>
                </li>
              </>
            )}
          </ul>
        </div>
      </div>
    );
  };

  const renderRow = (res) => {
    const idStr = String(res.guest_reservation_id);
    const cur = entries[idStr] || {};
    const rental = rentalByPmsId[res.pms_id];
    // Reservations from the search API can come back without a category set
    // (especially regular guest stays); fall back to STA so the chip renders.
    const catKey = res.category || "STA";
    const cat = CATEGORY_META[catKey] || { label: catKey };
    const propertyTags =
      res.tags || res.tag_array || rental?.tags || [];
    const dateRange =
      res.check_in && res.check_out
        ? `${fmtDateRange(res.check_in)} - ${fmtDateRange(res.check_out)}`
        : res.check_in
        ? fmtDateRange(res.check_in)
        : "—";
    const activeStatus =
      INSPECTION_STATUSES.find((s) => cur.statuses?.[s.key])?.key || "";
    return (
      <tr
        key={`${idStr}-${res.pms_id}`}
        className={`rbr-inspection-row${activeStatus ? ` is-${activeStatus}` : ""}`}
      >
        <td className="rbr-inspection-property-cell">
          <div className="semi-bold-font">{res.pms_id || rental?.pms_id || "—"}</div>
          {(rental?.address || res.rental_address) && (
            <div className="secondary">
              {rental?.address || res.rental_address}
            </div>
          )}
          <ReservationNumber
            hostID={res?.host_id}
            pms={user?.details?.pms}
            resevationID={res?.reservation_id}
            nativeID={res?.native_id}
          />
          {res.source && <div className="secondary">{res.source}</div>}
          {propertyTags.length > 0 && (
            <div className="rbr-inspection-tags">
              {propertyTags.map((tag, i) => (
                <span
                  key={`${idStr}-tag-${tag.id ?? tag.name ?? i}`}
                  className="rbr-inspection-tag"
                >
                  {tag.name || String(tag)}
                </span>
              ))}
            </div>
          )}
        </td>
        <td className="rbr-inspection-guest-cell">
          <div>
            <span className={`rbr-inspection-cat-chip is-cat-${catKey}`}>
              {cat.label}
            </span>
          </div>
          <div className="secondary">{dateRange}</div>
          <div className="semi-bold-font">
            {[res.first_name, res.last_name].filter(Boolean).join(" ") || "—"}
          </div>
          {(res.formatted_phone || res.phone) && (
            <div className="secondary">{res.formatted_phone || res.phone}</div>
          )}
        </td>
        <td className="rbr-inspection-days-vacant">
          {(() => {
            const dv = daysVacantFor(res);
            if (dv === null) return <span className="secondary">—</span>;
            return (
              <span>
                {dv} day{dv === 1 ? "" : "s"}
              </span>
            );
          })()}
        </td>
        <td className="rbr-inspection-status-td">{renderStatusCell(res)}</td>
        <td className="rbr-inspection-note">
          {editingNoteId === idStr ? (
            <textarea
              autoFocus
              className="uk-textarea rbr-inspection-note-input"
              placeholder="Notes"
              rows={2}
              value={cur.note || ""}
              onChange={(e) => updateNote(res, e.target.value)}
              onBlur={() => setEditingNoteId(null)}
            />
          ) : cur.note ? (
            <div
              className="rbr-inspection-note-display"
              onClick={() => setEditingNoteId(idStr)}
              title="Click to edit"
            >
              {cur.note}
            </div>
          ) : (
            <button
              type="button"
              className="uk-button uk-button-link rbr-inspection-note-add"
              onClick={() => setEditingNoteId(idStr)}
            >
              + Add note
            </button>
          )}
        </td>
        <td className="hms-action-btn rbr-inspection-actions">
          {(res.formatted_phone || res.phone) && (
            <div className="uk-inline">
              <button
                type="button"
                className="uk-button"
                uk-toggle="target: #modal-guest-messages"
                onClick={() => handleSMS(res)}
                uk-tooltip="Send Text"
              >
                <Image src="/images/sms.svg" />
              </button>
            </div>
          )}
          <div className="uk-inline">
            <button
              type="button"
              className="uk-button"
              uk-tooltip="Mark Handled"
              onClick={() => toggleHandled(res)}
            >
              <img
                data-src={cur.handled ? "/images/fulfilled.svg" : "/images/unfulfilled.svg"}
                data-uk-img=""
                loading="lazy"
                src={cur.handled ? "/images/fulfilled.svg" : "/images/unfulfilled.svg"}
                alt=""
              />
            </button>
          </div>
        </td>
      </tr>
    );
  };

  // ----- render ----------------------------------------------------------
  const dayKeys = Object.keys(grouped);

  return (
    <section className="rbr-inspection">
      <div id="hms-page-title">
        <h1 className="uk-heading-small">Inspection Board</h1>
      </div>

      <div id="hms-main-body">
        {/* Toolbar */}
        <div className="uk-flex uk-flex-between uk-flex-middle uk-flex-wrap uk-margin-small-bottom rbr-inspection-toolbar">
          <div className="uk-flex uk-flex-middle uk-flex-wrap">
            <ul className="uk-tab uk-margin-remove-bottom uk-margin-small-right rbr-inspection-tabs" data-uk-tab>
              <li className={view === "daily" ? "uk-active" : ""}>
                <a href="#" onClick={(e) => { e.preventDefault(); setView("daily"); }}>Daily</a>
              </li>
              <li className={view === "weekly" ? "uk-active" : ""}>
                <a href="#" onClick={(e) => { e.preventDefault(); setView("weekly"); }}>Weekly</a>
              </li>
            </ul>

            <div className="uk-button-group uk-margin-small-right">
              <button
                className="uk-button uk-button-default"
                onClick={goPrev}
                uk-tooltip="Previous"
              >
                <span uk-icon="icon: chevron-left"></span>
              </button>
              <button
                className="uk-button uk-button-default"
                onClick={goToday}
              >
                Today
              </button>
              <button
                className="uk-button uk-button-default"
                onClick={goNext}
                uk-tooltip="Next"
              >
                <span uk-icon="icon: chevron-right"></span>
              </button>
            </div>

            <h4 className="uk-margin-remove uk-margin-small-left uk-margin-small-right">
              {headerLabel}
            </h4>
          </div>

          <div className="uk-flex uk-flex-middle">
            <div className="uk-margin-small-right rbr-inspection-tag-filter">
              <SearchableDropdown
                value={statusFilter}
                onChange={(val) => setStatusFilter(val)}
                placeholder="All Statuses"
                options={[
                  { value: "", label: "All Statuses" },
                  { value: "none", label: "No Status" },
                  ...INSPECTION_STATUSES.map((s) => ({
                    value: s.key,
                    label: s.label,
                  })),
                ]}
              />
            </div>
            <div className="uk-margin-small-right rbr-inspection-tag-filter">
              <SearchableDropdown
                value={tagFilter}
                onChange={(val) => setTagFilter(val)}
                placeholder="All Tags"
                options={[
                  { value: "", label: "All Tags" },
                  ...tagOptions.map((t) => ({
                    value: String(t.id),
                    label: t.name,
                  })),
                ]}
              />
            </div>
            {/*
            <button
              type="button"
              className="uk-button uk-button-default uk-button-small"
            >
              Legend
            </button>
            <div data-uk-dropdown="mode: click; pos: bottom-right" className="rbr-inspection-legend-dropdown">
              <div className="rbr-inspection-legend">
                {INSPECTION_STATUSES.map((s) => (
                  <span
                    key={s.key}
                    className={`rbr-inspection-legend-item is-${s.key}`}
                  >
                    {s.label}
                  </span>
                ))}
              </div>
            </div>
            */}                    
          </div>
        </div>

        {loading && (
          <div className="uk-text-center uk-padding-small">
            <div uk-spinner="ratio: 1"></div>
          </div>
        )}

        {!loading &&
          dayKeys.map((dayKey) => {
            const rows = grouped[dayKey];
            return (
              <div
                key={dayKey}
                className="rbr-inspection-day uk-margin-small-bottom"
              >
                <div className="rbr-inspection-day-header">
                  {fmtDateLong(dayKey)}
                  <span className="uk-text-meta uk-margin-small-left">
                    {rows.length} arrival{rows.length === 1 ? "" : "s"}
                  </span>
                </div>

                <div className="uk-overflow-auto">
                  <table className="uk-table uk-table-small uk-table-divider hms-table rbr-inspection-table">
                    <thead>
                      <tr>
                        <th>Property / Reservation Number</th>
                        <th>Check-in &amp; Out / Name / Phone</th>
                        <th>Days Vacant</th>
                        <th>Status</th>
                        <th>Notes</th>
                        <th className="actions">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.length === 0 && (
                        <tr>
                          <td
                            colSpan={6}
                            className="uk-text-center uk-text-meta uk-padding-small"
                          >
                            No arrivals
                          </td>
                        </tr>
                      )}
                      {rows.map(renderRow)}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
      </div>
      <GuestMessages />
    </section>
  );
};

export default Inspections;
